import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, BookText, Globe, FileType2, Play, Trash2, Clock, GraduationCap, Loader2, AlertTriangle, RotateCw } from "lucide-react";
import { ProgressRing } from "@/components/common/ProgressRing";
import {
    AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
    AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";

const ICONS = { pdf: FileText, epub: BookText, docx: FileType2, url: Globe, txt: FileText };

export const DocumentCard = ({ doc, index = 0, onDelete, onRetry }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const status = doc.status || "ready";
    const isProcessing = status === "processing";
    const isFailed = status === "failed";
    const isReady = !isProcessing && !isFailed;
    const Icon = ICONS[doc.source_type] || FileText;
    const pct = doc?.progress?.percent || 0;
    const completed = doc?.progress?.completed;
    const readLabel = pct > 0 && !completed ? t("card.resume") : completed ? t("card.reread") : t("card.read");

    const open = () => { if (isReady) navigate(`/read/${doc.id}`); };

    // Scanned PDFs run through AI Vision OCR; surface live per-page progress.
    const ocr = doc?.meta?.ocr;
    let processingText = t("card.processingHint");
    if (ocr && ocr.total) {
        processingText = ocr.done > 0
            ? t("card.ocrProgress", { done: ocr.done, total: ocr.total })
            : t("card.ocrHint");
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3), ease: [0.22, 1, 0.36, 1] }}
            whileHover={isReady ? { y: -3 } : undefined}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-surface-1 shadow-elev-1 transition-colors hover:bg-surface-2"
            data-testid={`document-card-${doc.id}`}
        >
            {/* Cover */}
            <button
                onClick={open}
                disabled={!isReady}
                data-testid={`card-cover-${doc.id}`}
                className={cn("relative h-24 w-full overflow-hidden text-left", isReady ? "cursor-pointer" : "cursor-default")}
            >
                <div className="absolute inset-0 bg-surface-3" />
                <div className="hero-wash absolute inset-0 opacity-70" />
                <div className="relative flex h-full items-center justify-between px-4">
                    <div className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-xl bg-surface-1/70 backdrop-blur",
                        isFailed ? "text-[hsl(var(--destructive))]" : "text-[hsl(var(--primary))]"
                    )}>
                        {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" /> : isFailed ? <AlertTriangle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                    </div>
                    {isReady && <ProgressRing value={completed ? 100 : pct} size={42} />}
                    {isProcessing && (
                        <span data-testid={`card-status-${doc.id}`} className="rounded-full bg-surface-1/70 px-2.5 py-1 text-[11px] font-medium text-t2 backdrop-blur">
                            {t("card.processing")}
                        </span>
                    )}
                    {isFailed && (
                        <span data-testid={`card-status-${doc.id}`} className="rounded-full bg-[hsl(var(--destructive))]/15 px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--destructive))] backdrop-blur">
                            {t("card.failed")}
                        </span>
                    )}
                </div>
            </button>

            {/* Body */}
            <div className="flex flex-1 flex-col p-4">
                <button onClick={open} disabled={!isReady} className={cn("text-left", isReady ? "cursor-pointer" : "cursor-default")}>
                    <h3 className="line-clamp-2 font-sans-display text-sm font-semibold text-t1">{doc.title}</h3>
                </button>

                {isProcessing ? (
                    <div className="mt-2 flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin text-[hsl(var(--primary))]" />
                        <span className="text-xs text-t3" data-testid={`card-processing-hint-${doc.id}`}>{processingText}</span>
                    </div>
                ) : isFailed ? (
                    <p className="mt-2 line-clamp-2 text-xs text-[hsl(var(--destructive))]">{doc.error || t("card.failedHint")}</p>
                ) : (
                    <div className="mt-2 flex items-center gap-3 text-t4">
                        <span className="mono-label uppercase">{doc.source_type}</span>
                        <span className="inline-flex items-center gap-1 text-xs"><Clock className="h-3 w-3" /> {doc.reading_minutes || 1} min</span>
                    </div>
                )}

                <div className="mt-4 flex items-center gap-2">
                    {isReady ? (
                        <>
                            <Button data-testid={`card-read-${doc.id}`} size="sm" className="flex-1 gap-1.5" onClick={open}>
                                <Play className="h-3.5 w-3.5" /> {readLabel}
                            </Button>
                            <Button data-testid={`card-study-${doc.id}`} variant="ghost" size="icon" className="text-t4 hover:text-[hsl(var(--primary))]" title={t("card.study")} onClick={() => navigate(`/study/${doc.id}`)}>
                                <GraduationCap className="h-4 w-4" />
                            </Button>
                        </>
                    ) : isProcessing ? (
                        <Button data-testid={`card-status-button-${doc.id}`} size="sm" variant="secondary" className="flex-1 gap-1.5 bg-surface-3/60" disabled>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("card.processing")}
                        </Button>
                    ) : (
                        <Button data-testid={`card-retry-${doc.id}`} size="sm" variant="secondary" className="flex-1 gap-1.5 bg-surface-3/60" onClick={() => onRetry?.(doc.id)}>
                            <RotateCw className="h-3.5 w-3.5" /> {t("common.retry")}
                        </Button>
                    )}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button data-testid={`card-delete-${doc.id}`} variant="ghost" size="icon" className="text-t4 hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="border-border bg-surface-1">
                            <AlertDialogHeader>
                                <AlertDialogTitle className="text-t1">{t("card.deleteTitle")}</AlertDialogTitle>
                                <AlertDialogDescription className="text-t3">{t("card.deleteBody")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel className="bg-surface-3">{t("card.cancel")}</AlertDialogCancel>
                                <AlertDialogAction data-testid={`confirm-delete-${doc.id}`} className="bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90" onClick={() => onDelete?.(doc.id)}>{t("card.deleteConfirm")}</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </motion.div>
    );
};
